import { computed, signal } from "@angular/core";
import { DroplistItem } from "../droplist";
import { PageTabsLayout } from "./page-tabs-layout";


export abstract class PageTabsLayoutPageService<TAB extends number, VIEW extends number> {

	private _layout = signal<PageTabsLayout | undefined>(undefined);
	public layout = this._layout.asReadonly();

	public collapsedChange = computed(() => {
		const layout = this._layout();
		const collapsed = layout ? !layout.tab1Expanded : false; // Handle null layout


		if (collapsed && this._selectedTab() && this._selectedTab() !== 1) {
			this.selectTab(<TAB>1);
		}
		return collapsed;

	});

	private state: { tab: TAB, view: VIEW };

	private _selectedTab = signal<TAB | undefined>(undefined);
	public selectedTab = this._selectedTab.asReadonly();

	private _selectedView = signal<VIEW | undefined>(undefined);
	public selectedView = this._selectedView.asReadonly();
	public selectedViewItem = computed(() => this.viewDroplistItems.find(item => item.uniqueId == this.selectedView()));



	constructor(
		/** Pass the name of the class that extends PageTabsLayoutPageService */
		private _viewDroplistItems: DroplistItem<never, number>[] = [],
		private pageServiceClassName: string,
		private initialTab: TAB,
		private initialView: VIEW,
	) {

		this.state = { tab: initialTab, view: initialView };

		this._selectedTab.set(this.initialTab);
		this._selectedView.set(this.initialView);

		window.addEventListener('popstate', event => {
			this.updateState(event.state[pageServiceClassName]);
		});

	}


	onViewDroplistChange(selectedView: DroplistItem | undefined) {
		if (selectedView) this.selectView(selectedView.uniqueId as VIEW);
	}

	public get viewDroplistItems(): DroplistItem<never, number>[] {
		return this._viewDroplistItems;
	}
	/**
	 * This function should be called in from the page's ngOnInit() function. It ensures the last state values
	 * are reinstated and handles query parameters if passed on the url query string. (e.g. ?tab=2&view=3)
	 */
	initializeUrl() {

		const state = this.state;

		//
		// If there are query params (e.g. ?tab=2&view=3) then we remove
		// them from the href and use the values to set the state. 
		//
		const href = location.href.split('?');

		if (href[1]) {
			const parms = href[1].toLowerCase().split('&');

			for (const parm of parms) {
				const parmParts = parm.split('=');
				const value = +parmParts[1]!;

				if (parmParts[0] == 'tab') state.tab = <TAB>value || this.initialTab;
				if (parmParts[0] == 'view') state.view = <VIEW>value || this.initialView;
			}
		}

		const historyState = { [this.pageServiceClassName]: state };

		history.replaceState(historyState, '', href[0]);
		this.updateState(state);
	}


	public selectTab(tabId: TAB) {
		if (this._selectedView()) this.updateUrl(tabId, this._selectedView()!);
	}


	public selectView(viewId: VIEW) {
		if (this._selectedTab()) this.updateUrl(this._selectedTab()!, viewId);
	}


	/**
	 * Updates the tab and view.
	 * @param changeTabOnlyIfTab1Expanded If true then the tab will not change if the first tab is standalone.
	 */
	public async selectTabAndView(tabId: TAB, viewId: VIEW, changeTabOnlyIfTab1Expanded = false) {

		if (changeTabOnlyIfTab1Expanded) {
			const layout = this.layout();
			if (layout && !layout.tab1Expanded) tabId = this.state.tab;
		}

		this.updateUrl(tabId, viewId);
	}


	private updateUrl(tab: TAB, view: VIEW) {

		console.log(`updateUrl(${tab}, ${view});`);

		if (this.state.tab == tab && this.state.view == view) return;

		const state = { tab, view };

		const historyState = { [this.pageServiceClassName]: state };

		history.pushState(historyState, '', location.href);
		this.updateState(state);
	}


	private updateState(state: { tab: TAB, view: VIEW }) {
		if (!state) return;
		this.state = state;
		this._selectedView.set(state.view);
		this._selectedTab.set(state.tab);
	}


	public changeLayout(layout: PageTabsLayout) {
		this._layout.set(layout);
	}
}